/**
 * ResultRankingRow Component
 * Row showing swing thumbnail, quality score, and rank
 * Sorted by quality (best to worst)
 */

import { Trophy, Medal, Award } from 'lucide-react';
import { SwingThumbnail } from './SwingThumbnail';
import { cn } from '@/lib/utils';

interface SwingRun {
  id: string;
  created_at: string;
  thumbnail_url: string | null;
  filename: string | null;
  swing_quality: number;
}

interface ResultRankingRowProps {
  swing: SwingRun;
  rank: number;
  className?: string;
  /** Show quality score column. Defaults to false since V3 analysis doesn't provide scoring. */
  showQuality?: boolean;
}

/**
 * Check if swing quality is a meaningful value (not default 0 or 100)
 */
function isQualityMeaningful(quality: number): boolean {
  return quality > 0 && quality < 100;
}

export function ResultRankingRow({
  swing,
  rank,
  className,
  showQuality = false
}: ResultRankingRowProps) {
  const getRankIcon = () => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-muted-foreground font-semibold">{rank}</span>;
  };

  const getQualityColor = (quality: number) => {
    if (quality >= 80) return 'text-success';
    if (quality >= 60) return 'text-info';
    if (quality >= 40) return 'text-warning';
    return 'text-error';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className={cn(
      "flex items-center gap-4 p-4 rounded-lg",
      "border border-border bg-card",
      "hover:bg-muted/50 transition-colors",
      className
    )}>
      {/* Rank */}
      <div className="flex-shrink-0">
        {getRankIcon()}
      </div>

      {/* Thumbnail */}
      <div className="flex-shrink-0 w-24">
        <SwingThumbnail
          id={swing.id}
          thumbnailUrl={swing.thumbnail_url}
          filename={swing.filename}
          hasAnalysis={true}
        />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">
              {swing.filename || `Swing ${swing.id.slice(0, 8)}`}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatDate(swing.created_at)}
            </p>
          </div>

          {/* Quality Score - only show if enabled and value is meaningful */}
          {showQuality && isQualityMeaningful(swing.swing_quality) && (
            <div className="flex-shrink-0 text-right">
              <div className={cn("text-2xl font-bold", getQualityColor(swing.swing_quality))}>
                {swing.swing_quality.toFixed(0)}
              </div>
              <div className="text-xs text-muted-foreground">Quality</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


